import { FC, forwardRef } from 'react';
import {
  Dialog,
  DialogContent,
  Stack,
  Button,
  Slide,
  Box,
  useMediaQuery,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useDialogs, DialogProps } from '@toolpad/core/useDialogs';
import { TransitionProps } from '@mui/material/transitions';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@core/features/auth';
import { useGetSpacing } from '@ui';
import { LanguageSelectDialog } from '@i18n';
import { ViewNavigationBar, ConfirmDialog } from '@components';

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>
) {
  return (
    <Slide
      direction="up"
      easing={{
        enter: 'cubic-bezier(0.66, 0, 0.34, 1)',
        exit: 'linear',
      }}
      ref={ref}
      {...props}
    />
  );
});

export const UserMenu: FC<DialogProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const getSpacing = useGetSpacing();
  const dialogs = useDialogs();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { deleteTokens, isGuest, email } = useAuthStore();
  console.log('UserMenu rendered', isGuest);
  const queryClient = useQueryClient();

  const handleLogout = async () => {
    const closeStatus = await dialogs.open(ConfirmDialog, {
      okText: t('signOut'),
      cancelText: t('cancel'),
      text: t('confirmSignOutMessage'),
    });

    if (closeStatus === 'confirmed') {
      deleteTokens();
      queryClient.removeQueries();
      onClose();
      navigate({
        to: '/',
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      fullWidth={true}
      maxWidth="md"
      fullScreen={fullScreen}
      slots={{
        transition: Transition,
      }}
    >
      <ViewNavigationBar onClose={() => onClose()} title={t('yourSettings')} />
      <DialogContent
        sx={{ backgroundColor: '#FFFFFF', padding: 6, paddingBottom: 10 }}
      >
        <Stack direction="column" alignItems="center" spacing={getSpacing('l')}>
          {!isGuest && email && (
            <Typography
              sx={{ fontSize: 18, fontWeight: 400, color: '#0032A0' }}
            >
              {email}
            </Typography>
          )}

          <Box>
            <Button onClick={handleLogout} variant="contained" color="primary">
              {t('signOut')}
            </Button>
          </Box>
          <Box>
            <Button
              onClick={() => dialogs.open(LanguageSelectDialog)}
              variant="contained"
              color="primary"
            >
              {t('changeLanguage')}
            </Button>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
